import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PlusIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import {
  fetchTools,
  fetchToolCategories,
  fetchToolBrands,
  fetchToolServiceOverview,
  fetchNextToolNumber,
  createTool,
  updateTool,
  duplicateTool,
} from "../../../api/tools";
import ToolDialog from "./ToolDialog";
import ToolCategoryDialog from "./ToolCategoryDialog";
import ToolBrandDialog from "./ToolBrandDialog";
import toast from "react-hot-toast";
import DashboardHeader from "../dashboardHeader";
import { ModuleFilter } from "../../utils/ModuleFilter";
import ModernEmployeeSelector from "../../utils/Selector/ModernEmployeeSelector";

function sortToolsByNumber(list) {
  return [...list].sort((a, b) => {
    const na =
      parseInt(String(a.tool_number || "").replace(/\D/g, ""), 10) || 0;
    const nb =
      parseInt(String(b.tool_number || "").replace(/\D/g, ""), 10) || 0;
    if (na !== nb) return na - nb;
    return String(a.tool_number || "").localeCompare(
      String(b.tool_number || ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });
}

function formatApiErrors(error) {
  const d = error?.response?.data;
  if (Array.isArray(d?.errors)) return d.errors.filter(Boolean).join("\n");
  if (typeof d?.errors === "string") return d.errors;
  if (d?.error && typeof d.error === "string") return d.error;
  return null;
}

const ToolsOverview = () => {
  const { t } = useTranslation();
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [serviceOverview, setServiceOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showToolDialog, setShowToolDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showBrandDialog, setShowBrandDialog] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [suggestedNextNumber, setSuggestedNextNumber] = useState("");
  const [quickAddTitle, setQuickAddTitle] = useState("");
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [assignPickerForTool, setAssignPickerForTool] = useState(null);

  const [filters, setFilters] = useState({
    query: "",
    statuses: [],
    modules: [],
    users: [],
    projects: [],
    customers: [],
    contacts: [],
    departments: [],
    categories: [],
    brands: [],
  });

  const refreshNextNumberHint = useCallback(async () => {
    try {
      const r = await fetchNextToolNumber();
      const n = r?.data?.next_tool_number;
      if (n != null) setSuggestedNextNumber(String(n));
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        filters.query ||
        filters.statuses.length > 0 ||
        filters.categories.length > 0 ||
        filters.users.length > 0 ||
        filters.brands.length > 0
      ) {
        handleSearchWithFilters(filters);
      } else {
        loadData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [toolsRes, categoriesRes, brandsRes, serviceRes] =
        await Promise.all([
          fetchTools({ per_page: 100 }),
          fetchToolCategories({ per_page: 100 }),
          fetchToolBrands({ per_page: 100 }),
          fetchToolServiceOverview(),
        ]);

      const list = toolsRes.data || [];
      setTools(sortToolsByNumber(list));
      setCategories(categoriesRes.data || []);
      setBrands(brandsRes.data || []);
      setServiceOverview(serviceRes.data);
      const metaN = toolsRes.meta?.next_tool_number;
      if (metaN != null) setSuggestedNextNumber(String(metaN));
      else await refreshNextNumberHint();
    } catch (error) {
      console.error("Error loading tools data:", error);
      const msg = formatApiErrors(error);
      toast.error(msg || t("Failed to load tools data"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    if (JSON.stringify(newFilters) === JSON.stringify(filters)) {
      return;
    }

    setFilters(newFilters);
  };

  const handleSearchWithFilters = async (searchFilters) => {
    setIsLoading(true);
    try {
      const controller = {
        query: searchFilters.query,
        status:
          searchFilters.statuses.length > 0 ? searchFilters.statuses[0] : "",
        category_id:
          searchFilters.categories.length > 0
            ? searchFilters.categories[0]
            : "",
        module:
          searchFilters.modules.length > 0 ? searchFilters.modules[0] : "",
        assignment:
          searchFilters.users.length > 0 ? searchFilters.users[0] : "",
        brand_id:
          searchFilters.brands.length > 0 ? searchFilters.brands[0] : "",
        per_page: 100,
      };

      const response = await fetchTools(controller);
      const list = response.data || [];
      setTools(sortToolsByNumber(list));
      const metaN = response.meta?.next_tool_number;
      if (metaN != null) setSuggestedNextNumber(String(metaN));
    } catch (error) {
      console.error("Error searching tools:", error);
      const msg = formatApiErrors(error);
      toast.error(msg || t("Failed to search tools"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToolCreated = (newTool) => {
    setTools((prev) => sortToolsByNumber([newTool, ...prev]));
    setShowToolDialog(false);
    setSelectedTool(null);
    refreshNextNumberHint();
  };

  const handleToolUpdated = (updatedTool) => {
    setTools((prev) =>
      sortToolsByNumber(
        prev.map((tool) => (tool.id === updatedTool.id ? updatedTool : tool))
      )
    );
    setShowToolDialog(false);
    setSelectedTool(null);
  };

  const commitQuickAdd = async () => {
    const title = quickAddTitle.trim();
    if (!title || quickAddSaving) return;
    setQuickAddSaving(true);
    try {
      const res = await createTool({
        title,
        status: "active",
        active: true,
      });
      const created = res.data;
      setTools((prev) => sortToolsByNumber([created, ...prev]));
      setQuickAddTitle("");
      toast.success(t("tool.tool_created"));
      refreshNextNumberHint();
    } catch (error) {
      console.error(error);
      const msg = formatApiErrors(error);
      toast.error(msg || t("Failed to save tool"));
    } finally {
      setQuickAddSaving(false);
    }
  };

  const handleDuplicateTool = async (toolRow) => {
    try {
      const res = await duplicateTool(toolRow.id);
      setTools((prev) => sortToolsByNumber([res.data, ...prev]));
      toast.success(t("tool.duplicate_created"));
      refreshNextNumberHint();
    } catch (error) {
      console.error(error);
      const msg = formatApiErrors(error);
      toast.error(msg || t("Failed to save tool"));
    }
  };

  const assignPickerSelectedEmployees = assignPickerForTool?.assigned_user
    ? [
        {
          id: assignPickerForTool.assigned_user.id,
          name:
            assignPickerForTool.assigned_user.name ||
            [
              assignPickerForTool.assigned_user.first_name,
              assignPickerForTool.assigned_user.last_name,
            ]
              .filter(Boolean)
              .join(" ") ||
            assignPickerForTool.assigned_user.email,
        },
      ]
    : [];

  const handleAssignFromPicker = async (selected) => {
    if (!assignPickerForTool?.id || !selected?.length) return;
    try {
      const res = await updateTool(assignPickerForTool.id, {
        assigned_user_id: selected[0].id,
      });
      setTools((prev) =>
        sortToolsByNumber(
          prev.map((x) => (x.id === res.data.id ? res.data : x))
        )
      );
      toast.success(t("tool.assign_saved"));
      setAssignPickerForTool(null);
    } catch (error) {
      console.error(error);
      const msg = formatApiErrors(error);
      toast.error(msg || t("Failed to save"));
    }
  };

  const handleCategoryCreated = (newCategory) => {
    setCategories([newCategory, ...categories]);
  };

  const handleCategoryUpdated = (updatedCategory) => {
    setCategories(
      categories.map((cat) =>
        cat.id === updatedCategory.id ? updatedCategory : cat
      )
    );
  };

  const handleCategoryDeleted = (categoryId) => {
    setCategories((prev) => prev.filter((c) => c.id !== categoryId));
  };

  const handleBrandCreated = (newBrand) => {
    setBrands([newBrand, ...brands]);
  };

  const handleBrandUpdated = (updatedBrand) => {
    setBrands(
      brands.map((brand) =>
        brand.id === updatedBrand.id ? updatedBrand : brand
      )
    );
  };

  const handleBrandDeleted = (brandId) => {
    setBrands((prev) => prev.filter((b) => b.id !== brandId));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "maintenance":
        return <WrenchScrewdriverIcon className="h-5 w-5 text-yellow-500" />;
      case "inactive":
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const headerActions = [
    {
      label: t("tool.add_tool"),
      icon: PlusIcon,
      action: () => {
        setSelectedTool(null);
        setShowToolDialog(true);
      },
      hidden: false,
      menu: false,
    },
    {
      label: t("tool.category.add_category"),
      icon: PlusIcon,
      action: () => setShowCategoryDialog(true),
      hidden: false,
      menu: false,
    },
    {
      label: t("tool.brand.add_brand"),
      icon: PlusIcon,
      action: () => setShowBrandDialog(true),
      hidden: false,
      menu: false,
    },
  ];

  if (isLoading && tools.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <DashboardHeader
      title="Verktøy Oversikt"
      subtitle="Administrer verktøy"
      actions={headerActions}
    >
      <div className="space-y-6 p-4">
        {serviceOverview && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t("tool.total_tools")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {serviceOverview.total_tools}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t("tool.active_tools")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {serviceOverview.active_tools}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t("tool.upcoming_services")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {serviceOverview.tools_due_soon?.length || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t("tool.overdue_services")}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {serviceOverview.tools_overdue?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-4 rounded-lg shadow border">
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            disabled={isLoading}
            acceptedStatuses={["active", "inactive", "maintenance", "retired"]}
            searchPlaceholder="Search tools..."
            filterTitle="Filter Tools"
            moduleOptions={[]}
            userOptions={[
              { value: "all", label: "All Users" },
              { value: "assigned", label: "Assigned" },
              { value: "unassigned", label: "Unassigned" },
            ]}
            projectOptions={[]}
            customerOptions={[]}
            contactOptions={[]}
            departmentOptions={[]}
            categoryOptions={categories.map((cat) => ({
              value: cat.id.toString(),
              label: cat.name,
            }))}
            brandOptions={brands.map((brand) => ({
              value: brand.id.toString(),
              label: brand.name,
            }))}
          />
        </div>

        <div className="bg-white shadow border rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      {t("tool.quick_row_label")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.title")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.number")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.category")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.next_service")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.assigned_user_column")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("tool.active_column")}
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">{t("Actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="bg-blue-50/40">
                    <td className="px-6 py-3 text-sm text-gray-600 font-medium whitespace-nowrap">
                      {t("tool.quick_new")}
                    </td>
                    <td className="px-6 py-3">
                      <input
                        type="text"
                        value={quickAddTitle}
                        onChange={(e) => setQuickAddTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Tab" || e.key === "Enter") {
                            if (!quickAddTitle.trim()) return;
                            e.preventDefault();
                            commitQuickAdd();
                          }
                        }}
                        disabled={quickAddSaving}
                        placeholder={t("tool.quick_add_placeholder")}
                        className="block w-full min-w-[12rem] rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      />
                    </td>
                    <td
                      className="px-6 py-3 text-xs text-gray-500 whitespace-nowrap"
                      title={t("tool.next_number_hint_title")}
                    >
                      {suggestedNextNumber
                        ? `→ ${suggestedNextNumber}`
                        : "—"}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-400">—</td>
                    <td className="px-6 py-3 text-sm text-gray-400">—</td>
                    <td className="px-6 py-3 text-sm text-gray-400">—</td>
                    <td className="px-6 py-3 text-sm text-gray-400">—</td>
                    <td className="px-6 py-3 text-sm text-gray-400">—</td>
                    <td className="px-6 py-3 text-right text-xs text-gray-400 whitespace-nowrap">
                      {t("tool.quick_add_tab_hint")}
                    </td>
                  </tr>
                  {tools.map((tool) => (
                    <tr key={tool.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        —
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {tool.title}
                        </div>
                        {tool.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {tool.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tool.tool_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tool.category?.name || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(tool.status)}
                          <span
                            className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              tool.status
                            )}`}
                          >
                            {t(`tool.status.${tool.status}`)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tool.next_service_date ? (
                          <span
                            className={
                              tool.service_overdue
                                ? "text-red-600 font-medium"
                                : tool.service_due_soon
                                ? "text-yellow-600 font-medium"
                                : ""
                            }
                          >
                            {new Date(
                              tool.next_service_date
                            ).toLocaleDateString()}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tool.assigned_user?.name ||
                          [
                            tool.assigned_user?.first_name,
                            tool.assigned_user?.last_name,
                          ]
                            .filter(Boolean)
                            .join(" ") ||
                          tool.assigned_user?.email ||
                          "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            tool.active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {tool.active ? t("Yes") : t("No")}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                          <Link
                            to={`/dashboard/tools/${tool.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t("tool.open")}
                          </Link>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTool(tool);
                              setShowToolDialog(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t("Edit")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setAssignPickerForTool(tool)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-900"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                            {t("tool.assign_short")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDuplicateTool(tool)}
                            className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            {t("tool.duplicate")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {tools.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t("tool.no_tools")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("Get started by creating a new tool")}
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => {
                      setSelectedTool(null);
                      setShowToolDialog(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {t("tool.add_tool")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <ToolDialog
          open={showToolDialog}
          onClose={() => {
            setShowToolDialog(false);
            setSelectedTool(null);
          }}
          tool={selectedTool}
          categories={categories}
          brands={brands}
          onToolCreated={handleToolCreated}
          onToolUpdated={handleToolUpdated}
          suggestedNextNumber={suggestedNextNumber}
        />

        <ToolCategoryDialog
          open={showCategoryDialog}
          onClose={() => {
            setShowCategoryDialog(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
          categories={categories}
          onCategoryCreated={handleCategoryCreated}
          onCategoryUpdated={handleCategoryUpdated}
          onCategoryDeleted={handleCategoryDeleted}
        />

        <ToolBrandDialog
          open={showBrandDialog}
          onClose={() => {
            setShowBrandDialog(false);
            setSelectedBrand(null);
          }}
          brand={selectedBrand}
          brands={brands}
          onBrandCreated={handleBrandCreated}
          onBrandUpdated={handleBrandUpdated}
          onBrandDeleted={handleBrandDeleted}
        />

        {assignPickerForTool ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              role="presentation"
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setAssignPickerForTool(null)}
              onKeyDown={() => {}}
            />
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl z-10">
              <ModernEmployeeSelector
                open={Boolean(assignPickerForTool)}
                selectedEmployees={assignPickerSelectedEmployees}
                onChange={handleAssignFromPicker}
                onClose={() => setAssignPickerForTool(null)}
                multiple={false}
                filters={[]}
              />
            </div>
          </div>
        ) : null}
      </div>
    </DashboardHeader>
  );
};

export default ToolsOverview;
